import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Users, LandPlot, Flag, ShieldCheck, ShieldAlert, MessageSquare, TrendingUp, AlertTriangle, Package, Bookmark } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('it-IT').format(n||0);

function KPI({ I, label, value, sub, to, tone = 'default' }) {
  const toneCls = tone==='alert' ? 'border-terracotta/60' : tone==='accent' ? 'border-accent/60' : 'border-border';
  const inner = (
    <div className={`rounded-2xl border ${toneCls} p-5 bg-card hover:-translate-y-0.5 transition-transform h-full`}>
      <I size={18} className="text-muted-foreground" strokeWidth={1.5}/>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-3">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-1 tracking-tighter">{fmt(value)}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>{inner}</Link> : inner;
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [act, setAct] = useState(null);
  useEffect(() => {
    api.get('/admin/dashboard').then(r => setD(r.data));
    api.get('/admin/activity', { params: { limit: 8 } }).then(r => setAct(r.data));
  }, []);

  if (!d) return <div className="text-muted-foreground text-sm">Caricamento…</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">Panoramica</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">Console di amministrazione</h1>
        <p className="mt-2 text-sm text-muted-foreground">Tutti i valori qui provengono in tempo reale dal database. Nessun dato è simulato.</p>
      </header>

      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Terreni · pubblicazione automatica</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPI I={LandPlot} label="Totali" value={d.listings_total} to="/admin/listings"/>
          <KPI I={ShieldCheck} label="Pubblicati" value={d.listings_published} to="/admin/listings?status=published" tone="accent"/>
          <KPI I={AlertTriangle} label="Rifiutati" value={d.listings_rejected} to="/admin/listings?status=rejected"/>
          <KPI I={AlertTriangle} label="Sospesi" value={d.listings_suspended} to="/admin/listings?status=suspended" tone={d.listings_suspended>0?'alert':'default'}/>
          <KPI I={TrendingUp} label="Nuovi 7g" value={d.listings_new_7d}/>
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Partite · marketplace prodotti</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI I={Package} label="Partite totali" value={d.batches_total} to="/admin/batches"/>
          <KPI I={ShieldCheck} label="Pubblicate" value={d.batches_published} to="/admin/batches?status=published" tone="accent"/>
          <KPI I={AlertTriangle} label="Sospese" value={d.batches_suspended} to="/admin/batches?status=suspended" tone={d.batches_suspended>0?'alert':'default'}/>
          <KPI I={TrendingUp} label="Nuove 7g" value={d.batches_new_7d}/>
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Utenti</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPI I={Users} label="Utenti registrati" value={d.users_total} to="/admin/users"/>
          <KPI I={ShieldCheck} label="Verificati" value={d.users_verified} to="/admin/users?verification=verified"/>
          <KPI I={ShieldAlert} label="Verifica in corso" value={d.users_pending_verification} to="/admin/verifications" tone={d.users_pending_verification>0?'alert':'default'}/>
          <KPI I={AlertTriangle} label="Sospesi" value={d.users_suspended} to="/admin/users?suspended=true"/>
          <KPI I={TrendingUp} label="Nuovi 7g" value={d.users_new_7d}/>
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Moderazione &amp; engagement</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPI I={Flag} label="Segnalazioni aperte" value={d.reports_open} to="/admin/reports?status=open" tone={d.reports_open>0?'alert':'default'}/>
          <KPI I={Flag} label="In revisione" value={d.reports_in_review} to="/admin/reports?status=in_review"/>
          <KPI I={Flag} label="Segnalazioni totali" value={d.reports_total} to="/admin/reports"/>
          <KPI I={MessageSquare} label="Conversazioni" value={d.conversations}/>
          <KPI I={Bookmark} label="Ricerche salvate" value={d.saved_searches}/>
        </div>
      </section>

      {act && (
        <section className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-3 border-b border-border font-medium">Ultimi terreni</div>
            {act.listings.length===0 ? <div className="p-5 text-sm text-muted-foreground">Nessun terreno</div> :
              <ul className="divide-y divide-border">
                {act.listings.map(l=>(
                  <li key={l.listing_id} className="px-5 py-3 flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{l.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{l.owner_name} · {new Date(l.created_at).toLocaleDateString('it-IT')}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-secondary shrink-0 ml-2">{l.status}</span>
                  </li>
                ))}
              </ul>}
          </div>
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-5 py-3 border-b border-border font-medium flex items-center justify-between">
              <span>Ultime azioni admin</span>
              <Link className="text-xs text-muted-foreground link-underline" to="/admin/audit-log">Tutto →</Link>
            </div>
            {act.audit.length===0 ? <div className="p-5 text-sm text-muted-foreground">Nessuna azione registrata</div> :
              <ul className="divide-y divide-border">
                {act.audit.map(a=>(
                  <li key={a.audit_id} className="px-5 py-3 text-sm">
                    <div className="truncate"><b>{a.admin_email}</b> · <span className="text-terracotta">{a.action}</span></div>
                    <div className="text-xs text-muted-foreground truncate">{a.target_type} · {a.target_id} · {new Date(a.created_at).toLocaleString('it-IT')}</div>
                  </li>
                ))}
              </ul>}
          </div>
        </section>
      )}
    </div>
  );
}
